import { useState, useEffect } from 'react';
import ReactPlayer from 'react-player';
import styled from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { Play } from '@hivebrite/design-system/Play';

import type {
  VideoComponentProps,
  VideoNewUIProps,
} from 'hb-builder/components/BuilderComponents/single/Video/types';
import { useSectionContext } from 'hb-builder/hooks';
import { VIDEO_TYPE_ATTRIBUTE } from 'hb-react/backoffice/types/builder/itemAttributes/videoItemAttributes';
import Embed from 'hb-react/shared/components/DesignSystem/Embed';

const VideoComponent = ({
  embed,
  handleVideoStart,
  isDisabled,
  isLoading,
  type,
  url,
  videoPlay,
  videoStart,
}: VideoComponentProps) => {
  const { nbFinalChildren } = useSectionContext();
  const isSectionFullWidth = nbFinalChildren === 1;

  if (type === VIDEO_TYPE_ATTRIBUTE.LINK || !type) {
    return (
      <Embed
        autoplay
        embed={embed}
        mediaAspectRatio={isSectionFullWidth ? undefined : 1.777778}
        withDescription={false}
        isDisabled={isDisabled}
        isLoading={isLoading}
        roundCorners="none"
      />
    );
  }

  return (
    <ReactPlayer
      className="player"
      url={url}
      width="100%"
      height="-webkit-fill-available"
      playing={videoPlay}
      controls={videoPlay || videoStart}
      onStart={handleVideoStart}
    />
  );
};

const VideoNewUI = ({
  embed,
  isDisabled,
  isLoading,
  type,
  url,
  videoImageSrc,
}: VideoNewUIProps) => {
  const [videoPlay, setVideoPlay] = useState(false);
  const [videoStart, setVideoStart] = useState(false);

  const showImage =
    type === VIDEO_TYPE_ATTRIBUTE.LINK ? !videoPlay : !videoPlay && !videoStart;

  useEffect(() => {
    // Accessibility: we are playing or pausing the video by pressing on space key
    const handleKeyDown = () => {
      if (!isDisabled) {
        setVideoPlay(!videoPlay);
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isDisabled, videoPlay]);

  const handlePlayVideo = (): void => {
    if (!isDisabled) {
      setVideoPlay(true);
    }
  };

  const handleVideoStart = (): void => setVideoStart(true);

  return (
    <Container>
      <WrapperPlay>
        {showImage && (
          <Play
            ariaLabel={__('Start the video preview')}
            onClick={handlePlayVideo}
          />
        )}
      </WrapperPlay>

      {videoImageSrc && showImage ? (
        <StyledImage $image={videoImageSrc} />
      ) : (
        <VideoComponent
          isDisabled={isDisabled}
          type={type}
          url={url}
          videoPlay={videoPlay}
          videoStart={videoStart}
          handleVideoStart={handleVideoStart}
          embed={embed}
          isLoading={isLoading}
        />
      )}
    </Container>
  );
};

export default VideoNewUI;

const WrapperPlay = styled(Box).attrs(() => ({
  position: 'absolute',
  top: '50%',
  right: '50%',
}))`
  z-index: 2;
  transform: translate(50%, -50%);
`;

const StyledImage = styled(Box).attrs(() => ({
  width: '100%',
}))`
  background-image: ${({ $image }: { $image?: string }) => `url(${$image})`};
  background-repeat: no-repeat;
  background-size: cover;
  object-fit: cover;
  background-position: center;
  aspect-ratio: 16 / 9;

  &:after {
    content: '';
    display: block;
    padding-bottom: 100%;
  }
`;

const Container = styled(Box).attrs(() => ({
  alignItems: 'stretch',
  justifyContent: 'flex-start',
  width: '100%',
}))`
  aspect-ratio: 16 / 9;
  overflow: hidden;
  position: relative;
`;
